import { observer } from 'mobx-react-lite'
import classnames from 'classnames'
import { Box, Button, Status, Switch, Typography } from 'shared/ui'
import { RadioOptions } from 'shared/ui/Radio'
import { LINK_HELP_HUBSPOT_CALENDAR } from 'shared/constants/links'
import { ChatbotBookingType } from 'entities/Chatbot'
import { SmartMeeting } from 'pages/chatbot/pages/agentBookAppointment/pages/connectCalendar/ui/SmartMeeting'
import { SelectValueDropdown } from 'pages/chatbot/pages/agentBookAppointment/pages/connectCalendar/ui/SelectValueDropdown'
import { LinkTriggerContent } from 'pages/chatbot/pages/agentBookAppointment/pages/connectCalendar/ui/LinkTriggerContent'
import { LinkDropdownCustomComponent } from 'pages/chatbot/pages/agentBookAppointment/pages/connectCalendar/ui/LinkDropdownCustomComponent'
import { useUnifiedAgentContext } from 'pages/chatbot/ui/UnifiedAgent/context/unifiedAgentContext'
import { GoogleCalendarDropdownCustomComponent } from 'pages/chatbot/ui/UnifiedAgent/ui/BookingSection/ui/GoogleCalendarDropdownCustomComponent/GoogleCalendarDropdownCustomComponent'
import { GoogleCalendarAvailabilitySection } from 'pages/chatbot/ui/UnifiedAgent/ui/BookingSection/ui/GoogleCalendarAvailabilitySection/GoogleCalendarAvailabilitySection'
import styles from './styles.module.scss'

export const BookingSection = observer(() => {
  const { bookingSectionStore } = useUnifiedAgentContext()
  const {
    bookingEnabled,
    setBookingEnabled,
    isConnectionSelected,
    selectedConnectionItem,
    isLoading,
    isMeetingLoading,
    connectionList,
    setConnection,
    meetingsList,
    setLink,
    eventLink,
    search,
    setSearch,
    hasMoreMeetings,
    isLoadingMoreMeetings,
    loadMoreMeetings,
    bookingType,
    setBookingType,
    isHubspotSelected,
    isGoogleCalendarSelected,
    smartBookingEnabled,
    setSmartBookingEnabled,
    connectionError,
    eventLinkError,
    resetError,
    googleCalendarPickerStore,
  } = bookingSectionStore
  const {
    loading: isGoogleCalendarLoading,
    selectedCalendarItem,
    googleCalendarError,
    resetError: resetGoogleCalendarError,
  } = googleCalendarPickerStore

  return (
    <div>
      <Box display={'flex'} alignItems={'flex-start'} justifyContent={'space-between'}>
        <Box display={'flex'} flexDirection={'column'} flexGrow={'1'}>
          <Typography variant={'body-lg-medium'} ariaLabel={'Booking'}>
            Booking
          </Typography>
          <Typography
            variant={'body-md-regular'}
            ariaLabel={'BookingDesc'}
            color={'var(--content-primary-secondary)'}
            marginTop={4}
          >
            Configure how meetings are scheduled and managed
          </Typography>
        </Box>
        <Box paddingTop={'4px'}>
          <Switch value={bookingEnabled} onChange={setBookingEnabled} size='middle' />
        </Box>
      </Box>

      {bookingEnabled && (
        <div className={classnames(styles.body, { [styles.hubspotSelected]: isHubspotSelected })}>
          <Box
            display={'flex'}
            flexDirection={'column'}
            gap={isHubspotSelected ? 0.5 : 1}
            marginBottom={'22px'}
          >
            <div className={styles.schedulingTitle}>
              <Typography
                variant={'text-input-label-small'}
                ariaLabel={'SchedulingAppLabel'}
                color={'var(--content-primary-secondary)'}
              >
                Scheduling app
              </Typography>
              {isHubspotSelected && (
                <Button
                  typeBtn='informative'
                  size='small'
                  contained='secondary'
                  text='HubSpot setup guide'
                  onClick={() => {
                    window.open(LINK_HELP_HUBSPOT_CALENDAR, '_blank')
                  }}
                  style={{ textDecoration: 'none' }}
                />
              )}
            </div>
            <SelectValueDropdown
              placeholder={'Select scheduling app'}
              isValueSelected={isConnectionSelected}
              selectedItem={selectedConnectionItem}
              isLoading={isLoading}
              itemsList={connectionList}
              setSelectedValue={setConnection}
              errorMessage={connectionError}
              ariaLabel={'SelectConnection'}
              removeError={() => resetError('connectionError')}
            />
          </Box>

          {isConnectionSelected && isGoogleCalendarSelected && (
            <div className={styles.sectionWrap}>
              <Typography
                variant={'text-input-label-small'}
                ariaLabel={'CalendarLabel'}
                color={'var(--content-primary-secondary)'}
              >
                Calendar
              </Typography>
              <SelectValueDropdown
                placeholder={'Select calendar'}
                isValueSelected={!!selectedCalendarItem}
                isLoading={isGoogleCalendarLoading}
                errorMessage={googleCalendarError}
                ariaLabel={'SelectGoogleCalendar'}
                triggerContent={<LinkTriggerContent link={selectedCalendarItem} />}
                customComponent={(onClose) => (
                  <GoogleCalendarDropdownCustomComponent
                    googleCalendarPickerStore={googleCalendarPickerStore}
                    onClose={onClose}
                    onSelectCalendar={() => resetError('eventLinkError')}
                  />
                )}
                removeError={() => resetGoogleCalendarError('googleCalendarError')}
              />
            </div>
          )}

          {isConnectionSelected && !isGoogleCalendarSelected && (
            <div className={styles.sectionWrap}>
              <Typography
                variant={'text-input-label-small'}
                ariaLabel={'EventLinkLabel'}
                color={'var(--content-primary-secondary)'}
              >
                Event link
              </Typography>
              <SelectValueDropdown
                placeholder={'Select link'}
                isValueSelected={!!eventLink}
                isLoading={isMeetingLoading}
                errorMessage={eventLinkError}
                ariaLabel={'SelectEventLink'}
                triggerContent={<LinkTriggerContent link={eventLink} />}
                customComponent={(onClose) => (
                  <LinkDropdownCustomComponent
                    onClose={onClose}
                    search={search}
                    setSearch={setSearch}
                    itemsList={meetingsList}
                    setItem={setLink}
                    removeError={() => resetError('eventLinkError')}
                    hasMore={hasMoreMeetings}
                    isLoadingMore={isLoadingMoreMeetings}
                    onLoadMore={loadMoreMeetings}
                  />
                )}
              />
            </div>
          )}

          {!isGoogleCalendarSelected && (
            <div className={styles.sectionWrap}>
              <Typography
                variant={'text-input-label-small'}
                ariaLabel={'MethodLabel'}
                color={'var(--content-primary-secondary)'}
              >
                Method
              </Typography>
              <RadioOptions
                variant={'squareInset'}
                options={[
                  {
                    id: ChatbotBookingType.TimeChat,
                    value: ChatbotBookingType.TimeChat,
                    text: 'Chat to book a time',
                    tooltip: {
                      TooltipProps: {
                        label:
                          'The AI Agent will text back and forth with the contact to schedule a time',
                        placement: 'top',
                        breakWord: true,
                        width: 246,
                      },
                    },
                  },
                  {
                    id: ChatbotBookingType.ShareLink,
                    value: ChatbotBookingType.ShareLink,
                    text: 'Share booking link',
                    tooltip: {
                      TooltipProps: {
                        label:
                          'The AI Agent will share the booking link so the contact can choose a time',
                        placement: 'top',
                        breakWord: true,
                        width: 246,
                      },
                    },
                    disabled: isHubspotSelected,
                    rightContent: isHubspotSelected ? (
                      <Status
                        title={'Coming soon'}
                        intent={'neutral'}
                        emphasis={'high'}
                        size={'label'}
                      />
                    ) : undefined,
                  },
                ]}
                active={bookingType}
                onChange={setBookingType}
              />
            </div>
          )}
          {isGoogleCalendarSelected && (
            <GoogleCalendarAvailabilitySection
              googleCalendarPickerStore={googleCalendarPickerStore}
            />
          )}
          <SmartMeeting active={smartBookingEnabled} onChange={setSmartBookingEnabled} />
        </div>
      )}
    </div>
  )
})
